import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Chip } from 'primereact/chip';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import HeroIdentity from '../../components/agentProfile/HeroIdentity';
import HighlightStrip from '../../components/agentProfile/HighlightStrip';
import SectionCard from '../../components/agentProfile/SectionCard';
import ExpertiseBadgesList from '../../components/agentProfile/ExpertiseBadgesList';
import InlineEditSidebar from '../../components/agentProfile/InlineEditSidebar';

import ProfileService from '../../services/agentProfile/ProfileService';
import LinkedInService from '../../services/agentProfile/LinkedInService';
import LinkedInSyncButton from '../../components/agentProfile/LinkedInSyncButton';
import { getLocationLabel } from '../../utils/serviceLocations';
import {
    PROFESSIONAL_ROLES,
    LOAN_PRODUCTS,
    SERVICE_AREA_GROUPS,
    EXPERTISE_GROUPS,
    SKILL_GROUPS,
    LANGUAGE_GROUPS,
    productLabel,
    roleLabel,
    expertiseLabel,
    skillLabel,
    languageLabel,
} from '../../components/agentProfile/profileCatalogs';

// ─── Helpers ─────────────────────────────────────────────────────────────
const monthShort = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const yearsBetween = (startIso, endIso) => {
    if (!startIso) return '';
    const start = new Date(startIso);
    const end = endIso ? new Date(endIso) : new Date();
    if (Number.isNaN(start.getTime())) return '';
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const yr = Math.floor(months / 12);
    const mo = months % 12;
    if (yr === 0) return `${mo} mo`;
    if (mo === 0) return `${yr} yr${yr > 1 ? 's' : ''}`;
    return `${yr} yr${yr > 1 ? 's' : ''} ${mo} mo`;
};

const expiryMeta = (cert) => {
    if (cert.does_not_expire || !cert.expiration_date) {
        return { label: 'No expiry', severity: 'success' };
    }
    const now = new Date();
    const exp = new Date(cert.expiration_date);
    const daysLeft = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: 'Expired', severity: 'danger' };
    if (daysLeft < 90) return { label: `Expires in ${daysLeft}d`, severity: 'warning' };
    return { label: `Valid until ${monthShort(cert.expiration_date)}`, severity: 'success' };
};

const dataSourceMeta = (ds) => {
    if (ds === 'linkedin') return { label: 'LinkedIn synced', icon: 'pi pi-linkedin' };
    if (ds === 'hybrid')   return { label: 'LinkedIn + Manual', icon: 'pi pi-linkedin' };
    return { label: 'Manual entry', icon: 'pi pi-user' };
};

const formatStateName = (state) => {
    if (!state) return '';
    return state.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ═════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════
const MyAgentProfile = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const moreMenuRef = useRef(null);
    const profilePhotoInputRef = useRef(null);
    const coverPhotoInputRef = useRef(null);

    // ── Profile state ───────────────────────────────────────────────────
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [linkedInStatus, setLinkedInStatus] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [photoTimestamp, setPhotoTimestamp] = useState(Date.now());
    const [showAllSkills, setShowAllSkills] = useState(false);

    // ── Inline-edit Sidebar state ───────────────────────────────────────
    const [editSection, setEditSection] = useState(null); // 'about'|'headline'|'focus'|'skills'|'languages'|null
    const [savingEdit, setSavingEdit] = useState(false);
    const [draftBio, setDraftBio] = useState('');
    const [draftHeadline, setDraftHeadline] = useState('');
    const [draftRoles, setDraftRoles] = useState([]);
    const [draftProducts, setDraftProducts] = useState([]);
    const [draftExpertise, setDraftExpertise] = useState([]);
    const [draftAreas, setDraftAreas] = useState([]);
    const [draftSkills, setDraftSkills] = useState([]);
    const [draftLanguages, setDraftLanguages] = useState([]);

    // ── Import-from-Profile dialog state ────────────────────────────────
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [userProfileData, setUserProfileData] = useState(null);
    const [loadingUserProfile, setLoadingUserProfile] = useState(false);
    const [importingProfile, setImportingProfile] = useState(false);
    const [selectedFields, setSelectedFields] = useState({
        full_name: true,
        phone: true,
        current_company: true,
        current_designation: true,
        location: true,
        service_areas: true,
    });

    // ── Initial data load ───────────────────────────────────────────────
    useEffect(() => {
        loadProfile();
        loadLinkedInStatus();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await ProfileService.getMyProfile();
            if (response.success) {
                setProfile(response.data);
                setPhotoTimestamp(Date.now());
            }
        } catch (error) {
            // 404 → no profile yet → redirect to wizard
            if (error.response && error.response.status === 404) {
                setProfile(null);
                setLoading(false);
                toast.current?.show({
                    severity: 'info',
                    summary: 'Profile Not Found',
                    detail: 'Redirecting to create your profile...',
                    life: 2000,
                });
                setTimeout(() => navigate('/app/create-profile'), 2000);
                return;
            }
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load profile',
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const loadLinkedInStatus = async () => {
        try {
            const response = await LinkedInService.getStatus();
            if (response.success) setLinkedInStatus(response.data);
        } catch (error) {
            // Silent — LinkedIn integration is optional and currently hidden.
        }
    };

    // ── Highlights strip — 4 stats merged into the /profile response ────
    const highlights = useMemo(() => {
        const stats = profile?.stats || {};
        return [
            { key: 'connections', label: 'Connections',     value: stats.connection_count ?? profile?.connection_count ?? 0, icon: 'pi-users',      color: 'blue' },
            { key: 'views',       label: 'Profile views',   value: stats.profile_views_week ?? 0,                              icon: 'pi-eye',        color: 'cyan',   unit: 'this week' },
            { key: 'posts',       label: 'My posts',        value: stats.posts_this_week ?? 0,                                 icon: 'pi-pencil',     color: 'green',  unit: 'this week' },
            { key: 'engagement',  label: 'Engagement',      value: stats.engagement_this_week ?? 0,                            icon: 'pi-heart-fill', color: 'pink',   unit: 'this week' },
        ];
    }, [profile]);

    // ── Skills sort: top skills first, then by endorsement count ────────
    const sortedSkills = useMemo(() => {
        return [...(profile?.skills || [])].sort((a, b) => {
            if (a.is_top_skill && !b.is_top_skill) return -1;
            if (!a.is_top_skill && b.is_top_skill) return 1;
            return (b.endorsement_count || 0) - (a.endorsement_count || 0);
        });
    }, [profile?.skills]);
    const visibleSkills = showAllSkills ? sortedSkills : sortedSkills.slice(0, 10);
    const skillsOverflow = sortedSkills.length - 10;

    // ─── Photo upload handlers (preserved from production) ──────────────
    const handleProfilePhotoClick = () => profilePhotoInputRef.current?.click();
    const handleCoverPhotoClick   = () => coverPhotoInputRef.current?.click();

    const handleProfilePhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!profile || !profile.id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Profile Not Found',
                detail: 'Please create your profile first before uploading a photo',
                life: 4000,
            });
            if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
            return;
        }
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            toast.current?.show({ severity: 'error', summary: 'Invalid File Type', detail: 'Please select a JPEG or PNG image', life: 3000 });
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.current?.show({ severity: 'error', summary: 'File Too Large', detail: 'Image must be less than 2MB', life: 3000 });
            return;
        }
        try {
            setUploadingPhoto(true);
            const response = await ProfileService.uploadProfilePhoto(file);
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Profile photo updated', life: 2500 });
                await loadProfile();
            } else {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to upload photo';
            toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: msg, life: 5000 });
        } finally {
            setUploadingPhoto(false);
            if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = '';
        }
    };

    const handleDeletePhoto = () => {
        confirmDialog({
            message: 'Are you sure you want to delete your profile photo?',
            header: 'Delete Profile Photo',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setUploadingPhoto(true);
                    const response = await ProfileService.deleteProfilePhoto();
                    if (response.success) {
                        toast.current?.show({ severity: 'success', summary: 'Profile photo removed', life: 2500 });
                        await loadProfile();
                    } else {
                        throw new Error(response.message || 'Delete failed');
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message || 'Failed to delete photo';
                    toast.current?.show({ severity: 'error', summary: 'Delete Failed', detail: msg, life: 5000 });
                } finally {
                    setUploadingPhoto(false);
                }
            },
        });
    };

    const handleCoverPhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.current?.show({ severity: 'error', summary: 'Invalid File', detail: 'Please select an image file', life: 3000 });
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            toast.current?.show({ severity: 'error', summary: 'File Too Large', detail: 'Image must be less than 3MB', life: 3000 });
            return;
        }
        try {
            setUploadingPhoto(true);
            const response = await ProfileService.uploadCoverPhoto(file);
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Cover photo updated', life: 2500 });
                await loadProfile();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Upload Failed',
                detail: error.response?.data?.message || 'Failed to upload cover photo',
                life: 3000,
            });
        } finally {
            setUploadingPhoto(false);
            if (coverPhotoInputRef.current) coverPhotoInputRef.current.value = '';
        }
    };

    const handleDeleteCoverPhoto = () => {
        confirmDialog({
            message: 'Are you sure you want to remove your cover photo?',
            header: 'Remove Cover Photo',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    setUploadingPhoto(true);
                    const response = await ProfileService.deleteCoverPhoto();
                    if (response.success) {
                        toast.current?.show({ severity: 'success', summary: 'Cover photo removed', life: 2500 });
                        await loadProfile();
                    } else {
                        throw new Error(response.message || 'Delete failed');
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message || 'Failed to remove cover photo';
                    toast.current?.show({ severity: 'error', summary: 'Delete Failed', detail: msg, life: 5000 });
                } finally {
                    setUploadingPhoto(false);
                }
            },
        });
    };

    // ─── Import from Profile (preserved from production) ────────────────
    const handleOpenImportDialog = async () => {
        setShowImportDialog(true);
        setLoadingUserProfile(true);
        try {
            const response = await ProfileService.getUserProfileDetails();
            if (response.success && response.data) {
                setUserProfileData(response.data);
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'No Profile Data',
                    detail: 'Please complete your main profile first',
                    life: 3000,
                });
                setShowImportDialog(false);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load profile data', life: 3000 });
            setShowImportDialog(false);
        } finally {
            setLoadingUserProfile(false);
        }
    };

    const handleFieldToggle = (field) => {
        setSelectedFields((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleImportProfile = async () => {
        if (!userProfileData) return;
        setImportingProfile(true);
        try {
            const basicInfo = userProfileData.basic_info || {};
            const establishmentInfo = userProfileData.establishment_info || {};
            const loanAgentDetails = userProfileData.loan_agent_details || {};
            const profileData = {};

            if (selectedFields.full_name) {
                const fullName = [basicInfo.first_name, basicInfo.last_name].filter(Boolean).join(' ');
                if (fullName) profileData.full_name = fullName;
            }
            if (selectedFields.phone && basicInfo.mobile_number) {
                profileData.phone = basicInfo.mobile_number;
            }
            if (selectedFields.current_company) {
                const company = establishmentInfo.establishment_name || basicInfo.company_name;
                if (company) profileData.current_company = company;
            }
            if (selectedFields.current_designation && establishmentInfo.designation) {
                profileData.current_designation = establishmentInfo.designation;
            }
            if (selectedFields.location) {
                if (basicInfo.city)  profileData.location_city  = basicInfo.city;
                if (basicInfo.state) profileData.location_state = formatStateName(basicInfo.state);
                profileData.location_country = 'India';
            }
            if (selectedFields.service_areas && loanAgentDetails.active_cities?.length) {
                profileData.service_areas = loanAgentDetails.active_cities;
            }

            const response = await ProfileService.saveProfile(profileData);
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Profile imported', life: 2500 });
                setShowImportDialog(false);
                await loadProfile();
            } else {
                throw new Error(response.message || 'Import failed');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Failed to import profile';
            toast.current?.show({ severity: 'error', summary: 'Import Failed', detail: msg, life: 5000 });
        } finally {
            setImportingProfile(false);
        }
    };

    // ─── Inline edit handlers ───────────────────────────────────────────
    const closeEdit = () => { if (!savingEdit) setEditSection(null); };

    const openAboutEdit = () => {
        setDraftBio(profile.bio || '');
        setEditSection('about');
    };
    const openHeadlineEdit = () => {
        setDraftHeadline(profile.headline || '');
        setEditSection('headline');
    };
    const openFocusEdit = () => {
        setDraftRoles((profile.roles || []).map((r) => r.role_type));
        setDraftProducts((profile.product_expertise || []).map((p) => p.product_type));
        setDraftExpertise(profile.expertise || []);
        setDraftAreas(profile.service_areas || []);
        setEditSection('focus');
    };
    const openSkillsEdit = () => {
        setDraftSkills((profile.skills || []).map((s) => s.skill_name));
        setEditSection('skills');
    };
    const openLanguagesEdit = () => {
        setDraftLanguages((profile.languages || []).map((l) => l.language_name));
        setEditSection('languages');
    };

    const handleSaveAbout = async () => {
        try {
            setSavingEdit(true);
            const response = await ProfileService.saveProfile({ bio: draftBio });
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'About updated', life: 2000 });
                await loadProfile();
                setEditSection(null);
            } else {
                throw new Error(response.message || 'Save failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Save Failed',
                detail: error.response?.data?.message || error.message,
                life: 4000,
            });
        } finally {
            setSavingEdit(false);
        }
    };

    const handleSaveHeadline = async () => {
        try {
            setSavingEdit(true);
            const response = await ProfileService.saveProfile({ headline: draftHeadline });
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Headline updated', life: 2000 });
                await loadProfile();
                setEditSection(null);
            } else {
                throw new Error(response.message || 'Save failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Save Failed',
                detail: error.response?.data?.message || error.message,
                life: 4000,
            });
        } finally {
            setSavingEdit(false);
        }
    };

    const handleSaveFocus = async () => {
        try {
            setSavingEdit(true);
            const payload = {
                roles: draftRoles.map((rt, idx) => ({
                    role_type: rt,
                    is_primary: idx === 0,
                    years_in_role: 0,
                })),
                product_expertise: draftProducts.map((pt) => ({
                    product_type: pt,
                    expertise_levels: [],
                    years_of_experience: 0,
                    total_deals: 0,
                })),
                expertise: draftExpertise,
                service_areas: draftAreas,
            };
            const response = await ProfileService.saveProfile(payload);
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Focus updated', life: 2000 });
                await loadProfile();
                setEditSection(null);
            } else {
                throw new Error(response.message || 'Save failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Save Failed',
                detail: error.response?.data?.message || error.message,
                life: 4000,
            });
        } finally {
            setSavingEdit(false);
        }
    };

    // Skills + Languages save via bulk `saveProfile` — backend's
    // saveRelatedData deletes all manual rows and re-inserts. The granular
    // ProfileService.addSkill/deleteSkill methods exist on the frontend but
    // there are no matching backend routes; sending one PUT with the full
    // array is the only working path.
    const handleSaveSkills = async () => {
        try {
            setSavingEdit(true);
            // Preserve top-skill flag + endorsement count on skills the user kept.
            const existingByName = new Map((profile.skills || []).map((s) => [s.skill_name, s]));
            const skillsPayload = draftSkills.map((name) => {
                const prev = existingByName.get(name);
                return prev
                    ? { skill_name: name, is_top_skill: !!prev.is_top_skill, endorsement_count: prev.endorsement_count || 0 }
                    : { skill_name: name, is_top_skill: false, endorsement_count: 0 };
            });
            const response = await ProfileService.saveProfile({ skills: skillsPayload });
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Skills updated', life: 2000 });
                await loadProfile();
                setEditSection(null);
            } else {
                throw new Error(response.message || 'Save failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Save Failed',
                detail: error.response?.data?.message || error.message || 'Failed to update skills',
                life: 4000,
            });
        } finally {
            setSavingEdit(false);
        }
    };

    const handleSaveLanguages = async () => {
        try {
            setSavingEdit(true);
            // Preserve any per-language metadata for languages the user kept.
            const existingByName = new Map((profile.languages || []).map((l) => [l.language_name, l]));
            const languagesPayload = draftLanguages.map((name) => {
                const prev = existingByName.get(name);
                return prev
                    ? { language_name: name, proficiency_level: prev.proficiency_level || 'professional_working' }
                    : { language_name: name, proficiency_level: 'professional_working' };
            });
            const response = await ProfileService.saveProfile({ languages: languagesPayload });
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Languages updated', life: 2000 });
                await loadProfile();
                setEditSection(null);
            } else {
                throw new Error(response.message || 'Save failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Save Failed',
                detail: error.response?.data?.message || error.message || 'Failed to update languages',
                life: 4000,
            });
        } finally {
            setSavingEdit(false);
        }
    };

    // ─── Hero "More" menu ───────────────────────────────────────────────
    const moreMenuItems = useMemo(() => {
        const items = [
            { label: 'Import from profile', icon: 'pi pi-download',  command: handleOpenImportDialog },
            { label: 'Share profile link',  icon: 'pi pi-share-alt', command: () => {
                navigator.clipboard?.writeText(window.location.href);
                toast.current?.show({ severity: 'info', summary: 'Profile link copied', life: 1800 });
            }},
        ];
        if (profile?.profile_photo_url_full) {
            items.push({ separator: true });
            items.push({
                label: 'Delete profile photo',
                icon: 'pi pi-trash',
                command: handleDeletePhoto,
                className: 'text-red-600',
            });
        }
        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.profile_photo_url_full]);

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — loading
    // ═════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <Skeleton width="100%" height="6rem" className="mb-3" />
                        <div className="flex gap-3">
                            <Skeleton shape="circle" size="5.5rem" />
                            <div className="flex-1">
                                <Skeleton width="60%" height="1.5rem" className="mb-2" />
                                <Skeleton width="80%" height="1rem" className="mb-2" />
                                <Skeleton width="40%" height="0.875rem" />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — empty state (no profile yet)
    // ═════════════════════════════════════════════════════════════════════
    if (!profile) {
        return (
            <>
                <Toast ref={toast} />
                <EmptyProfileState
                    onImport={handleOpenImportDialog}
                    onCreate={() => navigate('/app/create-profile')}
                />
                <ImportFromProfileDialog
                    visible={showImportDialog}
                    onHide={() => setShowImportDialog(false)}
                    loading={loadingUserProfile}
                    importing={importingProfile}
                    data={userProfileData}
                    selectedFields={selectedFields}
                    onFieldToggle={handleFieldToggle}
                    onImport={handleImportProfile}
                    formatStateName={formatStateName}
                />
            </>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // RENDER — profile exists
    // ═════════════════════════════════════════════════════════════════════
    const dsMeta = dataSourceMeta(profile.data_source);

    const handleLinkedInSync = async () => {
        toast.current?.show({
            severity: 'info',
            summary: 'Syncing',
            detail: 'Syncing profile with LinkedIn...',
            life: 3000,
        });
        await loadProfile();
        await loadLinkedInStatus();
    };

    const heroActions = (
        <>
            <Menu model={moreMenuItems} popup ref={moreMenuRef} />
            {/* LinkedIn sync — kept hidden behind {false && ...} like
                production. When the LinkedIn integration is re-enabled,
                flip this flag (or replace with linkedInStatus.is_connected). */}
            {false && linkedInStatus && (
                <LinkedInSyncButton
                    linkedInStatus={linkedInStatus}
                    onSyncComplete={handleLinkedInSync}
                />
            )}
            <Button
                icon="pi pi-pencil"
                text
                rounded
                size="small"
                tooltip="Edit profile"
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => navigate('/app/edit-profile')}
                aria-label="Edit profile"
            />
            <Button
                icon="pi pi-share-alt"
                text
                rounded
                size="small"
                tooltip="Share"
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => {
                    navigator.clipboard?.writeText(window.location.href);
                    toast.current?.show({ severity: 'info', summary: 'Profile link copied', life: 1500 });
                }}
                aria-label="Share profile"
            />
            <Button
                icon="pi pi-ellipsis-h"
                text
                rounded
                size="small"
                tooltip="More"
                tooltipOptions={{ position: 'bottom' }}
                onClick={(e) => moreMenuRef.current?.toggle(e)}
                aria-label="More actions"
            />
        </>
    );

    const dataSourceTag = profile.data_source ? (
        <Chip
            label={dsMeta.label}
            icon={dsMeta.icon}
            className="text-xs"
        />
    ) : null;

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Hidden file inputs */}
            <input
                ref={profilePhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                style={{ display: 'none' }}
                onChange={handleProfilePhotoUpload}
            />
            <input
                ref={coverPhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                style={{ display: 'none' }}
                onChange={handleCoverPhotoUpload}
            />

            <div className="grid lending-network-polish">
                <div className="col-12">
                    {/* ── 1. HERO ─────────────────────────────────────── */}
                    <HeroIdentity
                        profile={{
                            ...profile,
                            // bust both caches so a freshly uploaded image
                            // shows immediately
                            profile_photo_url_full: profile.profile_photo_url_full
                                ? `${profile.profile_photo_url_full}?t=${photoTimestamp}`
                                : null,
                            cover_photo_url_full: profile.cover_photo_url_full
                                ? `${profile.cover_photo_url_full}?t=${photoTimestamp}`
                                : null,
                        }}
                        actions={heroActions}
                        onAvatarClick={handleProfilePhotoClick}
                        onCoverClick={handleCoverPhotoClick}
                        onCoverDelete={handleDeleteCoverPhoto}
                        uploadingPhoto={uploadingPhoto}
                        uploadingCover={uploadingPhoto}
                        dataSourceTag={dataSourceTag}
                    />

                    {/* ── 2. PROFILE COMPLETION (only when < 100%) ────── */}
                    {profile.profile_completed_percentage < 100 && (
                        <Card
                            className="shadow-1 mb-3"
                            pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
                        >
                            <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                                <div className="flex-grow-1">
                                    <div className="flex justify-content-between align-items-baseline mb-1">
                                        <span className="text-900 font-semibold text-xs md:text-sm">
                                            Your profile is {profile.profile_completed_percentage}% complete
                                        </span>
                                        <Tag
                                            value={profile.profile_completed_percentage >= 80 ? 'Almost there' : 'Keep going'}
                                            severity={profile.profile_completed_percentage >= 80 ? 'success' : 'info'}
                                            className="text-xs"
                                            style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}
                                        />
                                    </div>
                                    <ProgressBar
                                        value={profile.profile_completed_percentage}
                                        showValue={false}
                                        style={{ height: '6px' }}
                                        aria-label="Profile completion"
                                    />
                                    <div className="text-500 text-xs mt-1">
                                        A complete profile gets 3× more connection requests.
                                    </div>
                                </div>
                                <Button
                                    label="Complete"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    size="small"
                                    onClick={() => navigate('/app/edit-profile')}
                                    className="flex-shrink-0"
                                />
                            </div>
                        </Card>
                    )}

                    {/* ── 3. HIGHLIGHTS STRIP ─────────────────────────── */}
                    <div className="mb-3">
                        <HighlightStrip items={highlights} />
                    </div>

                    {/* ── 4. ABOUT ────────────────────────────────────── */}
                    <SectionCard
                        icon="pi-info-circle"
                        title="About"
                        onEdit={openAboutEdit}
                        className="mb-3"
                    >
                        {profile.bio ? (
                            <CollapsibleBio text={profile.bio} />
                        ) : (
                            <EmptyInline
                                icon="pi-info-circle"
                                message="Add a short bio so agents know who they're connecting with."
                                ctaLabel="Add bio"
                                onClick={openAboutEdit}
                            />
                        )}
                    </SectionCard>

                    {/* ── 5. FOCUS ────────────────────────────────────── */}
                    <SectionCard
                        icon="pi-tag"
                        title="Focus"
                        subtitle="Roles, products, and areas you serve"
                        onEdit={openFocusEdit}
                        className="mb-3"
                    >
                        <ExpertiseBadgesList
                            label="Roles"
                            items={(profile.roles || []).map((r) => roleLabel(r.role_type))}
                            color="blue"
                            showStar
                        />
                        <ExpertiseBadgesList
                            label="Product expertise"
                            items={(profile.product_expertise || []).map((p) => productLabel(p.product_type))}
                            color="indigo"
                        />
                        <ExpertiseBadgesList
                            label="Specializations"
                            items={(profile.expertise || []).map((e) => expertiseLabel(e))}
                            color="teal"
                        />
                        <ExpertiseBadgesList
                            label="Service areas"
                            items={(profile.service_areas || []).map((s) => getLocationLabel(s) || s)}
                            color="purple"
                        />
                        <ExpertiseBadgesList
                            label="Companies I work with"
                            items={profile.associated_companies || []}
                            color="orange"
                        />
                        {(profile.roles?.length || 0) === 0
                            && (profile.product_expertise?.length || 0) === 0
                            && (profile.expertise?.length || 0) === 0
                            && (profile.service_areas?.length || 0) === 0 && (
                            <EmptyInline
                                icon="pi-tag"
                                message="Add your focus areas so agents can find you."
                                ctaLabel="Set focus"
                                onClick={openFocusEdit}
                            />
                        )}
                    </SectionCard>

                    {/* ── 6. EXPERIENCE ───────────────────────────────── */}
                    <SectionCard
                        icon="pi-briefcase"
                        title="Experience"
                        onEdit={() => navigate('/app/edit-profile?step=work')}
                        className="mb-3"
                    >
                        {profile.work_experiences?.length > 0 ? (
                            <ul className="list-none p-0 m-0">
                                {profile.work_experiences.map((item, idx) => (
                                    <li key={item.id}>
                                        <div className="flex gap-3 py-2">
                                            <Avatar
                                                label={(item.company_name || '??').slice(0, 2).toUpperCase()}
                                                shape="square"
                                                style={{
                                                    width: '2.75rem',
                                                    height: '2.75rem',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    backgroundColor: 'var(--surface-100)',
                                                    color: 'var(--primary-700)',
                                                    flexShrink: 0,
                                                    borderRadius: '6px',
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <ExperienceContent item={item} />
                                            </div>
                                        </div>
                                        {idx < profile.work_experiences.length - 1 && <Divider className="my-0" />}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <EmptyInline
                                icon="pi-briefcase"
                                message="Add your past roles so agents can see your journey."
                                ctaLabel="Add experience"
                                onClick={() => navigate('/app/edit-profile?step=work')}
                            />
                        )}
                    </SectionCard>

                    {/* ── 7. EDUCATION ────────────────────────────────── */}
                    <SectionCard
                        icon="pi-book"
                        title="Education"
                        onEdit={() => navigate('/app/edit-profile?step=education')}
                        className="mb-3"
                    >
                        {profile.education?.length > 0 ? (
                            <ul className="list-none p-0 m-0">
                                {profile.education.map((item, idx) => (
                                    <li key={item.id}>
                                        <div className="flex gap-3 py-2">
                                            <Avatar
                                                icon="pi pi-graduation-cap"
                                                shape="square"
                                                style={{
                                                    width: '2.75rem',
                                                    height: '2.75rem',
                                                    backgroundColor: 'var(--surface-100)',
                                                    color: 'var(--primary-700)',
                                                    flexShrink: 0,
                                                    borderRadius: '6px',
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <EducationContent item={item} />
                                            </div>
                                        </div>
                                        {idx < profile.education.length - 1 && <Divider className="my-0" />}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <EmptyInline
                                icon="pi-book"
                                message="Add your education history."
                                ctaLabel="Add education"
                                onClick={() => navigate('/app/edit-profile?step=education')}
                            />
                        )}
                    </SectionCard>

                    {/* ── 8. CERTIFICATIONS ───────────────────────────── */}
                    <SectionCard
                        icon="pi-verified"
                        title="Licenses & certifications"
                        onEdit={() => navigate('/app/edit-profile?step=certs')}
                        className="mb-3"
                    >
                        {profile.certifications?.length > 0 ? (
                            <div className="grid">
                                {profile.certifications.map((cert) => {
                                    const meta = expiryMeta(cert);
                                    return (
                                        <div key={cert.id} className="col-12 md:col-6">
                                            <div className="surface-border border-1 border-round p-3 h-full flex flex-column gap-1">
                                                <div className="flex align-items-start justify-content-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-900 font-semibold text-sm line-height-3">
                                                            {cert.certification_name}
                                                        </div>
                                                        <div className="text-700 text-sm">{cert.issuing_organization}</div>
                                                    </div>
                                                    <Tag
                                                        value={meta.label}
                                                        severity={meta.severity}
                                                        className="text-xs flex-shrink-0"
                                                        style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}
                                                    />
                                                </div>
                                                <div className="text-500 text-xs">
                                                    Issued {monthShort(cert.issue_date)}
                                                </div>
                                                {cert.credential_url && (
                                                    <div className="mt-auto pt-2">
                                                        <Button
                                                            label="View credential"
                                                            icon="pi pi-external-link"
                                                            text
                                                            size="small"
                                                            onClick={() => window.open(cert.credential_url, '_blank', 'noopener')}
                                                            className="p-0"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyInline
                                icon="pi-verified"
                                message="Add certifications to build trust."
                                ctaLabel="Add certification"
                                onClick={() => navigate('/app/edit-profile?step=certs')}
                            />
                        )}
                    </SectionCard>

                    {/* ── 9. SKILLS ───────────────────────────────────── */}
                    <SectionCard
                        icon="pi-star"
                        title="Skills"
                        subtitle={sortedSkills.length ? `${sortedSkills.length} skills` : undefined}
                        onEdit={openSkillsEdit}
                        className="mb-3"
                    >
                        {sortedSkills.length > 0 ? (
                            <>
                                <div className="flex flex-wrap gap-2">
                                    {visibleSkills.map((s) => (
                                        <div
                                            key={s.id}
                                            className="border-round px-2 py-1 flex align-items-center gap-2 transition-colors transition-duration-150"
                                            style={{
                                                backgroundColor: s.is_top_skill ? 'var(--primary-color)' : 'var(--blue-50)',
                                                color: s.is_top_skill ? 'var(--primary-color-text)' : 'var(--blue-900)',
                                                border: s.is_top_skill ? 'none' : '1px solid var(--blue-200)',
                                            }}
                                        >
                                            {s.is_top_skill && (
                                                <i
                                                    className="pi pi-star-fill"
                                                    style={{ fontSize: '0.65rem' }}
                                                    aria-hidden="true"
                                                />
                                            )}
                                            <span className="text-sm font-medium">{skillLabel(s.skill_name)}</span>
                                            {s.endorsement_count > 0 && (
                                                <span
                                                    className="text-xs flex align-items-center gap-1"
                                                    style={{ opacity: 0.85 }}
                                                >
                                                    <i
                                                        className="pi pi-thumbs-up-fill"
                                                        style={{ fontSize: '0.6rem' }}
                                                        aria-hidden="true"
                                                    />
                                                    {s.endorsement_count}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {skillsOverflow > 0 && (
                                    <div className="mt-2">
                                        <Button
                                            label={showAllSkills ? 'Show less' : `Show all ${sortedSkills.length} skills`}
                                            text
                                            size="small"
                                            onClick={() => setShowAllSkills(!showAllSkills)}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <EmptyInline
                                icon="pi-star"
                                message="Add skills to help agents find you."
                                ctaLabel="Add skill"
                                onClick={openSkillsEdit}
                            />
                        )}
                    </SectionCard>

                    {/* ── 10. LANGUAGES ───────────────────────────────── */}
                    <SectionCard
                        icon="pi-globe"
                        title="Languages"
                        onEdit={openLanguagesEdit}
                        className="mb-3"
                    >
                        {profile.languages?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {profile.languages.map((l) => (
                                    <Chip
                                        key={l.id}
                                        label={languageLabel(l.language_name)}
                                        icon="pi pi-globe"
                                        className="text-sm"
                                    />
                                ))}
                            </div>
                        ) : (
                            <EmptyInline
                                icon="pi-globe"
                                message="Add languages you can serve clients in."
                                ctaLabel="Add language"
                                onClick={openLanguagesEdit}
                            />
                        )}
                    </SectionCard>
                </div>
            </div>

            {/* ── Inline-edit Sidebars ────────────────────────────────── */}
            <InlineEditSidebar
                visible={editSection === 'about'}
                onHide={closeEdit}
                title="Edit about"
                onSave={handleSaveAbout}
                saveLoading={savingEdit}
            >
                <div className="flex flex-column gap-2">
                    <label className="text-900 text-xs md:text-sm font-medium" htmlFor="bio-input">Bio</label>
                    <InputTextarea
                        id="bio-input"
                        value={draftBio}
                        onChange={(e) => setDraftBio(e.target.value)}
                        rows={6}
                        autoResize
                        maxLength={600}
                        className="w-full p-inputtext-sm"
                        placeholder="Share what you do, who you help, and what makes you different."
                    />
                    <div className="text-500 text-xs text-right">{draftBio.length}/600</div>
                </div>
            </InlineEditSidebar>

            <InlineEditSidebar
                visible={editSection === 'headline'}
                onHide={closeEdit}
                title="Edit headline"
                onSave={handleSaveHeadline}
                saveLoading={savingEdit}
            >
                <div className="flex flex-column gap-2">
                    <label className="text-900 text-xs md:text-sm font-medium" htmlFor="headline-input">Headline</label>
                    <InputText
                        id="headline-input"
                        value={draftHeadline}
                        onChange={(e) => setDraftHeadline(e.target.value)}
                        className="w-full p-inputtext-sm"
                        placeholder="e.g. Senior DSA · Home Loans Specialist"
                    />
                </div>
            </InlineEditSidebar>

            <InlineEditSidebar
                visible={editSection === 'focus'}
                onHide={closeEdit}
                title="Edit focus"
                onSave={handleSaveFocus}
                saveLoading={savingEdit}
            >
                <div className="flex flex-column gap-3">
                    <SidebarField label="Roles">
                        <MultiSelect
                            value={draftRoles}
                            options={PROFESSIONAL_ROLES}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setDraftRoles(e.value)}
                            display="chip"
                            filter
                            placeholder="Pick your roles"
                            className="w-full p-inputtext-sm"
                        />
                    </SidebarField>
                    <SidebarField label="Product expertise" help="Up to 6 — agents will search by these">
                        <MultiSelect
                            value={draftProducts}
                            options={LOAN_PRODUCTS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setDraftProducts(e.value.slice(0, 6))}
                            display="chip"
                            filter
                            maxSelectedLabels={3}
                            placeholder="Pick your products"
                            className="w-full p-inputtext-sm"
                        />
                    </SidebarField>
                    <SidebarField label="Specializations" help="Your niche within those products">
                        <MultiSelect
                            value={draftExpertise}
                            options={EXPERTISE_GROUPS}
                            optionLabel="label"
                            optionValue="value"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            onChange={(e) => setDraftExpertise(e.value)}
                            display="chip"
                            filter
                            filterPlaceholder="Search specializations…"
                            placeholder="Pick specializations"
                            className="w-full p-inputtext-sm"
                        />
                    </SidebarField>
                    <SidebarField label="Service areas" help="Cities, states, or pan-India">
                        <MultiSelect
                            value={draftAreas}
                            options={SERVICE_AREA_GROUPS}
                            optionLabel="label"
                            optionValue="value"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            onChange={(e) => setDraftAreas(e.value)}
                            display="chip"
                            filter
                            filterPlaceholder="Search cities…"
                            maxSelectedLabels={5}
                            placeholder="Pick service areas"
                            className="w-full p-inputtext-sm"
                        />
                    </SidebarField>
                </div>
            </InlineEditSidebar>

            <InlineEditSidebar
                visible={editSection === 'skills'}
                onHide={closeEdit}
                title="Edit skills"
                onSave={handleSaveSkills}
                saveLoading={savingEdit}
            >
                <div className="flex flex-column gap-2">
                    <SidebarField
                        label="Skills"
                        help={`${draftSkills.length} selected · top skills appear first on your profile`}
                    >
                        <MultiSelect
                            value={draftSkills}
                            options={SKILL_GROUPS}
                            optionLabel="label"
                            optionValue="value"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            onChange={(e) => setDraftSkills(e.value)}
                            display="chip"
                            filter
                            filterPlaceholder="Search skills…"
                            maxSelectedLabels={5}
                            placeholder="Pick from the catalog"
                            className="w-full p-inputtext-sm"
                        />
                    </SidebarField>
                </div>
            </InlineEditSidebar>

            <InlineEditSidebar
                visible={editSection === 'languages'}
                onHide={closeEdit}
                title="Edit languages"
                onSave={handleSaveLanguages}
                saveLoading={savingEdit}
            >
                <div className="flex flex-column gap-2">
                    <SidebarField label="Languages" help={`${draftLanguages.length} selected`}>
                        <MultiSelect
                            value={draftLanguages}
                            options={LANGUAGE_GROUPS}
                            optionLabel="label"
                            optionValue="value"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            onChange={(e) => setDraftLanguages(e.value)}
                            display="chip"
                            filter
                            filterPlaceholder="Search languages…"
                            maxSelectedLabels={5}
                            placeholder="Pick languages you speak"
                            className="w-full p-inputtext-sm"
                        />
                    </SidebarField>
                </div>
            </InlineEditSidebar>

            {/* ── Import-from-Profile dialog (preserved from production) ── */}
            <ImportFromProfileDialog
                visible={showImportDialog}
                onHide={() => setShowImportDialog(false)}
                loading={loadingUserProfile}
                importing={importingProfile}
                data={userProfileData}
                selectedFields={selectedFields}
                onFieldToggle={handleFieldToggle}
                onImport={handleImportProfile}
                formatStateName={formatStateName}
            />
        </>
    );
};

// ═════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════
const ExperienceContent = ({ item }) => (
    <div>
        <div className="flex align-items-center gap-2 flex-wrap">
            <span className="text-900 font-semibold text-sm">{item.job_title}</span>
            {item.is_current && (
                <Tag value="Current" severity="success" className="text-xs" style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem' }} />
            )}
        </div>
        <div className="text-700 text-sm">{item.company_name}</div>
        <div className="text-500 text-xs mt-1">
            {monthShort(item.start_date)} — {item.is_current ? 'Present' : monthShort(item.end_date)}
            {' · '}
            {yearsBetween(item.start_date, item.is_current ? null : item.end_date)}
            {item.location ? ` · ${item.location}` : ''}
        </div>
        {item.description && (
            <p className="text-600 text-xs md:text-sm mt-2 mb-0 line-height-3">{item.description}</p>
        )}
    </div>
);

const EducationContent = ({ item }) => (
    <div>
        <div className="flex align-items-center gap-2 flex-wrap">
            <span className="text-900 font-semibold text-sm">
                {item.degree_type ? item.degree_type.replace(/_/g, ' ').toUpperCase() : ''}
                {item.field_of_study ? ` in ${item.field_of_study}` : ''}
            </span>
            {item.is_ongoing && (
                <Tag value="Ongoing" severity="info" className="text-xs" style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem' }} />
            )}
        </div>
        <div className="text-700 text-sm">{item.institution_name}</div>
        <div className="text-500 text-xs mt-1">
            {monthShort(item.start_date)} — {item.is_ongoing ? 'Present' : monthShort(item.end_date)}
            {item.grade ? ` · Grade: ${item.grade}` : ''}
        </div>
    </div>
);

const CollapsibleBio = ({ text }) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = text.length > 240;
    const displayText = expanded || !isLong ? text : `${text.slice(0, 240)}…`;
    return (
        <div>
            <p className="text-700 text-xs md:text-sm line-height-3 m-0 white-space-pre-line">
                {displayText}
            </p>
            {isLong && (
                <Button
                    label={expanded ? 'Show less' : 'Show more'}
                    text
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    className="p-0 mt-1"
                />
            )}
        </div>
    );
};

const SidebarField = ({ label, help, children }) => (
    <div>
        <label className="text-900 text-xs md:text-sm font-medium mb-1 block">{label}</label>
        {children}
        {help && <div className="text-500 text-xs mt-1">{help}</div>}
    </div>
);

const EmptyInline = ({ icon, message, ctaLabel, onClick }) => (
    <div className="flex flex-column align-items-center text-center py-4 gap-2">
        <div
            className="flex align-items-center justify-content-center border-circle"
            style={{ width: '3rem', height: '3rem', backgroundColor: 'var(--surface-100)' }}
        >
            <i className={`pi ${icon} text-500`} style={{ fontSize: '1.1rem' }} aria-hidden="true" />
        </div>
        <p className="text-600 text-sm m-0">{message}</p>
        {ctaLabel && (
            <Button label={ctaLabel} icon="pi pi-plus" size="small" outlined severity="secondary" onClick={onClick} />
        )}
    </div>
);

// ─── Empty state when no profile exists yet ──────────────────────────────
const EmptyProfileState = ({ onImport, onCreate }) => (
    <div className="grid">
        <div className="col-12">
            <Card
                className="shadow-1"
                pt={{ body: { className: 'p-4 md:p-6' }, content: { className: 'p-0' } }}
            >
                <div className="flex flex-column align-items-center text-center gap-3 mb-4">
                    <div
                        className="flex align-items-center justify-content-center border-circle"
                        style={{ width: '5rem', height: '5rem', backgroundColor: 'var(--primary-100)' }}
                    >
                        <i
                            className="pi pi-user-plus text-primary"
                            style={{ fontSize: '2rem' }}
                            aria-hidden="true"
                        />
                    </div>
                    <div>
                        <h2 className="text-900 font-bold text-lg md:text-xl m-0 mb-2">
                            Create your agent profile
                        </h2>
                        <p
                            className="text-600 text-sm m-0 line-height-3"
                            style={{ maxWidth: '32rem' }}
                        >
                            Build your public profile so borrowers and agents can find you.
                            Takes 5 minutes — import from your main profile to skip most of the typing.
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-content-center">
                        <Button label="Import from profile" icon="pi pi-download" onClick={onImport} />
                        <Button label="Create manually" severity="secondary" outlined onClick={onCreate} />
                    </div>
                </div>

                <Divider />

                <div className="grid mt-2">
                    {[
                        { icon: 'pi-shield', title: 'Build credibility', desc: 'Showcase your experience, certifications, and endorsements.' },
                        { icon: 'pi-users',  title: 'Connect with peers', desc: 'Network with other financial professionals.' },
                        { icon: 'pi-bolt',   title: 'Quick setup',        desc: 'Import from your profile or fill manually in minutes.' },
                        { icon: 'pi-pencil', title: 'Always editable',    desc: 'Update your profile any time as you grow.' },
                    ].map((f, i) => (
                        <div key={i} className="col-12 md:col-6 p-2">
                            <div className="flex gap-2">
                                <div
                                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                    style={{ width: '2rem', height: '2rem', backgroundColor: 'var(--primary-50)' }}
                                >
                                    <i
                                        className={`pi ${f.icon} text-primary`}
                                        style={{ fontSize: '0.9rem' }}
                                        aria-hidden="true"
                                    />
                                </div>
                                <div>
                                    <div className="text-900 font-semibold text-sm">{f.title}</div>
                                    <div className="text-500 text-xs line-height-3 mt-1">{f.desc}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    </div>
);

// ─── Import-from-Profile dialog (preserved from production) ──────────────
const ImportFromProfileDialog = ({
    visible, onHide, loading, importing, data, selectedFields,
    onFieldToggle, onImport, formatStateName,
}) => (
    <Dialog
        visible={visible}
        onHide={onHide}
        header={
            <div className="flex align-items-center gap-2">
                <i className="pi pi-download text-primary" />
                <span>Import from Profile</span>
            </div>
        }
        style={{ width: '500px' }}
        breakpoints={{ '768px': '95vw' }}
        modal
        className="p-fluid"
        footer={
            <div className="flex justify-content-end gap-2">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={onHide}
                    disabled={importing}
                />
                <Button
                    label="Import Selected"
                    icon="pi pi-check"
                    onClick={onImport}
                    loading={importing}
                    disabled={loading || !data}
                />
            </div>
        }
    >
        {loading ? (
            <div className="flex flex-column gap-3">
                <Skeleton width="100%" height="3rem" />
                <Skeleton width="100%" height="3rem" />
                <Skeleton width="100%" height="3rem" />
                <Skeleton width="100%" height="3rem" />
            </div>
        ) : data ? (
            <div className="flex flex-column gap-3">
                <p className="text-600 mt-0 mb-3">
                    Select the fields you want to import to your lending network profile:
                </p>

                {(data.basic_info?.first_name || data.basic_info?.last_name) && (
                    <ImportFieldRow
                        id="import_name"
                        icon="pi-user"
                        label="Full Name"
                        value={[data.basic_info?.first_name, data.basic_info?.last_name].filter(Boolean).join(' ')}
                        checked={selectedFields.full_name}
                        onChange={() => onFieldToggle('full_name')}
                    />
                )}

                {data.basic_info?.mobile_number && (
                    <ImportFieldRow
                        id="import_phone"
                        icon="pi-phone"
                        label="Phone"
                        value={data.basic_info.mobile_number}
                        checked={selectedFields.phone}
                        onChange={() => onFieldToggle('phone')}
                    />
                )}

                {(data.establishment_info?.establishment_name || data.basic_info?.company_name) && (
                    <ImportFieldRow
                        id="import_company"
                        icon="pi-building"
                        label="Company"
                        value={data.establishment_info?.establishment_name || data.basic_info?.company_name}
                        checked={selectedFields.current_company}
                        onChange={() => onFieldToggle('current_company')}
                    />
                )}

                {data.establishment_info?.designation && (
                    <ImportFieldRow
                        id="import_designation"
                        icon="pi-id-card"
                        label="Designation"
                        value={data.establishment_info.designation}
                        checked={selectedFields.current_designation}
                        onChange={() => onFieldToggle('current_designation')}
                    />
                )}

                {(data.basic_info?.city || data.basic_info?.state) && (
                    <ImportFieldRow
                        id="import_location"
                        icon="pi-map-marker"
                        label="Location"
                        value={[
                            data.basic_info?.city,
                            formatStateName(data.basic_info?.state),
                            'India',
                        ].filter(Boolean).join(', ')}
                        checked={selectedFields.location}
                        onChange={() => onFieldToggle('location')}
                    />
                )}

                {data.loan_agent_details?.active_cities?.length > 0 && (
                    <ImportFieldRow
                        id="import_service_areas"
                        icon="pi-globe"
                        label="Service Areas"
                        value={(() => {
                            const cities = data.loan_agent_details.active_cities;
                            const head = cities.slice(0, 3).join(', ');
                            return cities.length > 3 ? `${head} +${cities.length - 3} more` : head;
                        })()}
                        checked={selectedFields.service_areas}
                        onChange={() => onFieldToggle('service_areas')}
                    />
                )}

                {!data.basic_info?.first_name
                  && !data.basic_info?.mobile_number
                  && !data.establishment_info?.establishment_name && (
                    <div className="text-center py-4">
                        <i className="pi pi-info-circle text-4xl text-400 mb-3" />
                        <p className="text-600 mt-0 mb-0">No profile data found to import.</p>
                        <p className="text-500 text-sm mt-2 mb-0">Please complete your main profile first.</p>
                    </div>
                )}
            </div>
        ) : (
            <div className="text-center py-4">
                <i className="pi pi-exclamation-triangle text-4xl text-orange-500 mb-3" />
                <p className="text-600 mt-0 mb-0">Unable to load profile data.</p>
            </div>
        )}
    </Dialog>
);

const ImportFieldRow = ({ id, icon, label, value, checked, onChange }) => (
    <div className="surface-100 border-round p-3">
        <div className="flex align-items-center gap-3">
            <Checkbox inputId={id} checked={checked} onChange={onChange} />
            <label htmlFor={id} className="flex-1 cursor-pointer">
                <div className="flex align-items-center gap-2">
                    <i className={`pi ${icon} text-primary`} />
                    <span className="font-semibold">{label}</span>
                </div>
                <p className="text-600 text-sm mt-1 mb-0">{value}</p>
            </label>
        </div>
    </div>
);

export default MyAgentProfile;
